import React, {RefObject, useContext} from "react";
import FlexBox from "view/components/Layout/FlexBox";
import clsx from "clsx";
import {Box, Button, Typography, useTheme} from "@material-ui/core";
import {
  ActiveCells,
  border,
} from "view/components/GridResponseBlock/CustomizeTable/tools";
import TextareaAutosize from "react-textarea-autosize";
import AlertIcon from "view/images/AlertIcon";
import DeleteIcon from "@material-ui/icons/Delete";
import {grey} from "@material-ui/core/colors";
import {GridForm} from "ts/api/task";
import type {IGridSchema} from "ts/api/grid-schema";
import {UseFormReturn} from "react-hook-form";
import {FormContext} from "view/components/Form";
import useStyles from "./styles";

type RowTitlesProps = {
  viewOnly?: boolean;
  isNotViewOnlyAndResponse?: boolean;
  isActive?: boolean;
  isResponseMode?: boolean;
  rowIndex: number;
  activeCells?: ActiveCells;
  rows: IGridSchema["rows"];
  name: string;
  textareaRef: RefObject<HTMLTextAreaElement>;
  makeRowMandatory: (index: number) => void;
  lastRowName: string;
  /** Renders the delete-row control inline, right after the row name. */
  showDelete?: boolean;
  isDeleteDisabled?: boolean;
  removeRow?: (index: number) => void;
};

function RowTitles({
  viewOnly,
  isNotViewOnlyAndResponse,
  isActive,
  rowIndex,
  activeCells,
  isResponseMode,
  rows,
  name,
  textareaRef,
  makeRowMandatory,
  lastRowName,
  showDelete,
  isDeleteDisabled,
  removeRow,
}: RowTitlesProps) {
  const classes = useStyles();
  const theme = useTheme();
  const form = useContext(FormContext) as UseFormReturn<GridForm>;

  const {watch, setValue, clearErrors} = form;

  const additionalRowNames = watch("additionalRowNames");
  const addedRowIndex = rowIndex - rows.length;
  const isAddedRow = addedRowIndex >= 0;
  const isDisabled = viewOnly || (isResponseMode ? !isAddedRow : false);

  const changeState = (value: string) => {
    if (isAddedRow) {
      const additionalRowsCopy = additionalRowNames
        ? [...additionalRowNames]
        : [];
      const addedRowIndex = rowIndex - rows.length;
      additionalRowsCopy[addedRowIndex] = value;
      setValue("additionalRowNames", additionalRowsCopy, {
        shouldDirty: true,
      });
      // This name isn't a registered field, so nothing re-validates it: drop
      // the "name your added rows" error the submit attempt raised as soon as
      // the row actually has a name.
      if (value.trim()) {
        clearErrors(`additionalRowNames.${addedRowIndex}`);
      }
    } else {
      setValue(`gridSchema.rows.${rowIndex}.name`, value, {
        shouldDirty: true,
      });
    }
  };

  return (
    <>
      <FlexBox
        className={clsx(
          classes.rootHeadCell,
          viewOnly && classes.viewOnlyRootHeadCell
        )}
      >
        {isNotViewOnlyAndResponse && <Box className={classes.marginBox} />}
        <FlexBox
          style={{
            borderRight: isActive ? border() : undefined,
          }}
          className={classes.headCell}
        >
          {isNotViewOnlyAndResponse && (
            <Box style={{position: "relative"}}>
              <Box className={classes.rowIndex}>
                <Typography
                  style={{
                    color:
                      activeCells?.rowIndex === rowIndex
                        ? theme.palette.primary.main
                        : undefined,
                  }}
                  className={classes.index}
                >
                  {rowIndex + 1}
                </Typography>
              </Box>
            </Box>
          )}
          <FlexBox className={classes.rowInputWithActions} alignCenter>
            <TextareaAutosize
              onKeyDown={(e) => {
                if (e.shiftKey && e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  const value = isAddedRow
                    ? `${additionalRowNames?.[addedRowIndex] || ""}\n`
                    : `${rows?.[rowIndex]?.name || ""}\n`;
                  return changeState(value);
                }
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  (e.target as HTMLElement)?.blur();
                }
              }}
              onChange={(e) => {
                const {value} = e.target;
                changeState(value);
              }}
              ref={textareaRef}
              value={lastRowName}
              className={clsx(
                classes.headCellInput,
                !lastRowName?.trim() && classes.headCellInputEmptyValue
              )}
              disabled={isDisabled}
              name={name || ""}
            />
            {isNotViewOnlyAndResponse && (
              <Button
                className={classes.makeRowMandatoryButton}
                onClick={() => makeRowMandatory(rowIndex)}
              >
                <AlertIcon />
              </Button>
            )}
            {showDelete && removeRow && (
              <Button
                disabled={isDeleteDisabled}
                className={classes.deleteRowButtonInline}
                onClick={() => removeRow(rowIndex)}
              >
                <DeleteIcon
                  className={clsx(
                    classes.icon,
                    isDeleteDisabled && classes.disableDeleteRowSVG
                  )}
                  htmlColor={grey[500]}
                />
              </Button>
            )}
          </FlexBox>
        </FlexBox>
      </FlexBox>
    </>
  );
}

export default RowTitles;
